import { useEffect, useState } from "react";
import dayjs from "dayjs";

import Chart from "react-apexcharts";
import { FaSquareFull } from "react-icons/fa";

import Spinner from "react-bootstrap/Spinner";
import Popover from "react-bootstrap/Popover";

import { useApi } from "@/hooks/useApi";
import { addFetus } from "@/utils/initialValues/addFetus";
import { dilationAndDeLeePlanOptions } from "@/utils/charts/dilationAndDeLeePlan";
import { heartRateOptions } from "@/utils/charts/heartRate";
import { transformData } from "@/utils/transformData";
import { paginatedPartogramRecord } from "@/utils/mappings/partogramRecord";
import { ContractionsPerSecond } from "@/enums/ContractionsPerSecond";
import { AmnioticSac } from "@/enums/AmnioticSac";
import { AmnioticFluid, AmnioticFluidMessages } from "@/enums/AmnioticFluid";

import { PartogramView } from "./PartogramView";
import { ContractionsPerSecondIcons } from "./ContractionsPerSecondIcons";

import "./styles.scss";

type AddFetusInitialValues = typeof addFetus;

type PartogramRecord = AddFetusInitialValues & {
	id: number;
	attendance_id: number;
	created_at: string;
	last_modified_by: number;
	product: Product | null;
	examiner: Examiner | null;
};

export type PaginatedPartogramRecords = GenericPagination<PartogramRecord>;

type PargogramFetusChartsProps = {
	fetusId: number;
	isActive: boolean;
};

export function PartogramFetusCharts({ fetusId, isActive }: PargogramFetusChartsProps) {
	const { api } = useApi();

	const [isLoading, setIsLoading] = useState(true);
	const [partogramRecords, setPartogramRecords] = useState<PaginatedPartogramRecords>(
		{} as PaginatedPartogramRecords
	);

	async function fetchPartogramRecords(next?: boolean) {
		try {
			setIsLoading(true);

			const baseApiUrl = `/partogram/${fetusId}`;
			const url = next ? partogramRecords.links?.next || baseApiUrl : baseApiUrl;

			const { data } = await api.get(url);
			const transformedData = transformData<PaginatedPartogramRecords>(
				data,
				paginatedPartogramRecord
			);

			const results = next
				? [...(partogramRecords.results || []), ...transformedData.results]
				: transformedData.results;
			setPartogramRecords({ ...transformedData, results });
		} catch (error) {
			console.log(error);
		} finally {
			setIsLoading(false);
		}
	}

	useEffect(() => {
		if (fetusId && isActive) {
			fetchPartogramRecords();
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fetusId, isActive]);

	const getXAxisCategories = () => {
		const partogramResults = partogramRecords.results;

		const formatCreatedAt = (createdAt: string) => [
			dayjs(createdAt).format("DD/MM/YYYY"),
			dayjs(createdAt).format("HH:mm"),
		];

		return partogramResults?.map((record) => formatCreatedAt(record.created_at));
	};

	const getDilationSeries = () => {
		const formatDilation = (dilation: string) => (dilation === null ? null : Number(dilation));

		return partogramRecords.results?.map((record) => formatDilation(record.dilation));
	};

	const getDeLeePlanSeries = () => {
		const formatDeLeePlan = (deLeePlan: string) =>
			deLeePlan === null ? null : Number(deLeePlan) + 1;

		return partogramRecords.results?.map((record) => formatDeLeePlan(record.delee_plan));
	};

	const dilation = dilationAndDeLeePlanOptions({
		data: partogramRecords,
		xAxisCategories: getXAxisCategories(),
		dilation: getDilationSeries(),
		deLeePlan: getDeLeePlanSeries(),
	});

	const getHeartRateSeries = () => {
		const formatHeartRate = (heartRate: string) =>
			heartRate === null ? null : Number(heartRate);

		return partogramRecords.results?.map((record) => formatHeartRate(record.heart_rate));
	};

	const heartRate = heartRateOptions({ data: partogramRecords, heartRate: getHeartRateSeries() });

	return isLoading ? (
		<div className="d-flex flex-center">
			<Spinner animation="border" variant="primary" />
		</div>
	) : (
		<>
			{partogramRecords.links?.next && (
				<div className="mb-4">
					Exibindo apenas os primeiros{" "}
					{partogramRecords.meta?.per_page * partogramRecords.meta?.current_page}{" "}
					registros do feto.{" "}
					<span
						className="text-primary"
						role="button"
						onClick={() => fetchPartogramRecords(true)}
					>
						Mostrar mais
					</span>
				</div>
			)}

			<div>
				<Chart type="line" options={dilation.options} series={dilation.series} />
			</div>

			<div>
				<Chart type="area" options={heartRate.options} series={heartRate.series} />
			</div>

			<div className="ps-7 pe-20 d-flex gap-9">
				<div className="d-flex align-items-center invisible">
					<span className="partogram-fetus-chart-legend">Contrações</span>
				</div>
				<div className="fs-5 d-flex text-nowrap align-items-center gap-4">
					<span>Legenda:</span>
					<span>
						0-19 seg <ContractionsPerSecondIcons.ZeroToNineTeen />
					</span>
					<span>
						20-39 seg <ContractionsPerSecondIcons.TwentyToThirtyNine />
					</span>
					<span>
						≥40 seg <ContractionsPerSecondIcons.Forty />
					</span>
				</div>
			</div>

			<div className="ps-7 pe-20 d-flex align-items-center gap-9">
				<div className="d-flex align-items-center">
					<span className="partogram-fetus-chart-legend">Contrações</span>
				</div>
				<PartogramView.Table>
					{Array.from({ length: 6 }, (_, index) => (
						<tr key={`contractions-per-second-row-${index}`}>
							{partogramRecords.results?.map((record) => {
								const currentInversedIndex = 5 - index;
								const currentInversedContractionsPerSecond =
									record.contractions_per_second[currentInversedIndex];

								return (
									<td
										key={`contractions-per-second-column-${record.id}`}
										className="text-center align-middle"
									>
										<i className="fs-1">
											{currentInversedContractionsPerSecond ? (
												ContractionsPerSecondIcons[
													currentInversedContractionsPerSecond as ContractionsPerSecond
												]()
											) : (
												// this is to keep the table aligned
												<FaSquareFull className="invisible" />
											)}
										</i>
									</td>
								);
							})}
						</tr>
					))}
				</PartogramView.Table>
			</div>

			<PartogramView>
				<PartogramView.Label>Bolsa</PartogramView.Label>
				<PartogramView.Table>
					<tr>
						{partogramRecords.results?.map((record) => {
							const amnioticSac = String(record.amniotic_sac);

							return (
								<PartogramView.TableData
									key={`amniotic-sac-${record.id}`}
									textSize="lg"
								>
									{amnioticSac === AmnioticSac.Intact ? (
										<span>I</span>
									) : amnioticSac === AmnioticSac.Ruptured ? (
										<span>R</span>
									) : (
										// this is to keep the table aligned
										<span className="invisible">R</span>
									)}
								</PartogramView.TableData>
							);
						})}
					</tr>
				</PartogramView.Table>
			</PartogramView>

			<PartogramView>
				<PartogramView.Label>L. Amniótico</PartogramView.Label>
				<PartogramView.Table>
					<tr>
						{partogramRecords.results?.map((record) => {
							const amnioticFluid = String(record.amniotic_fluid);

							return (
								<PartogramView.TableData key={`amniotic-fluid-${record.id}`}>
									{record.amniotic_fluid ? (
										AmnioticFluidMessages[amnioticFluid as AmnioticFluid]
									) : (
										// this is to keep the table aligned
										<span className="invisible">Com grumos</span>
									)}
								</PartogramView.TableData>
							);
						})}
					</tr>
				</PartogramView.Table>
			</PartogramView>

			<PartogramView>
				<PartogramView.Label>Medicação</PartogramView.Label>
				<PartogramView.Table>
					<tr>
						{partogramRecords.results?.map((record, index) => {
							const description = record.product?.description.toLowerCase();

							return (
								<PartogramView.TableData
									key={`medication-${record.id}`}
									overlay={
										record.product ? (
											<Popover id={`medication-popover-${record.id}`}>
												<Popover.Header className="popover-header-border">
													{index + 1}
												</Popover.Header>
												<Popover.Body>
													<div>
														Medicação:{" "}
														<strong className="text-capitalize">
															{description}
														</strong>
													</div>
												</Popover.Body>
											</Popover>
										) : undefined
									}
								>
									{record.product ? (
										<span className="text-capitalize text-limit" role="button">
											{description}
										</span>
									) : (
										<span className="invisible">Medicamento</span>
									)}
								</PartogramView.TableData>
							);
						})}
					</tr>
				</PartogramView.Table>
			</PartogramView>

			<PartogramView>
				<PartogramView.Label>Pressão Arterial</PartogramView.Label>
				<PartogramView.Table>
					<tr>
						{partogramRecords.results?.map((record) => {
							const systolicBloodPressure = record.systolic_blood_pressure;
							const diastolicBloodPressure = record.diastolic_blood_pressure;

							return (
								<PartogramView.TableData key={`blood-pressure-${record.id}`}>
									{systolicBloodPressure && diastolicBloodPressure ? (
										`${systolicBloodPressure}x${diastolicBloodPressure}`
									) : (
										<span className="invisible">000x00</span>
									)}
								</PartogramView.TableData>
							);
						})}
					</tr>
				</PartogramView.Table>
			</PartogramView>

			<PartogramView>
				<PartogramView.Label>Pulso</PartogramView.Label>
				<PartogramView.Table>
					<tr>
						{partogramRecords.results?.map((record) => {
							const pulse = record.pulse;

							return (
								<PartogramView.TableData key={`pulse-${record.id}`}>
									{pulse ?? <span className="invisible">00</span>}
								</PartogramView.TableData>
							);
						})}
					</tr>
				</PartogramView.Table>
			</PartogramView>

			<PartogramView>
				<PartogramView.Label>Temperatura</PartogramView.Label>
				<PartogramView.Table>
					<tr>
						{partogramRecords.results?.map((record) => {
							const temperature = record.temperature;

							return (
								<PartogramView.TableData key={`temperature-${record.id}`}>
									{temperature ? (
										String(Number(temperature))
									) : (
										<span className="invisible">00</span>
									)}
								</PartogramView.TableData>
							);
						})}
					</tr>
				</PartogramView.Table>
			</PartogramView>

			<PartogramView>
				<PartogramView.Label>Examinador</PartogramView.Label>
				<PartogramView.Table>
					<tr>
						{partogramRecords.results?.map((record, index) => {
							const name = record.examiner?.name.toLowerCase();

							return (
								<PartogramView.TableData
									key={`examiner-${record.id}`}
									overlay={
										record.examiner ? (
											<Popover id={`examiner-popover-${record.id}`}>
												<Popover.Header className="popover-header-border">
													{index + 1}
												</Popover.Header>
												<Popover.Body>
													<div>
														Examinador:{" "}
														<strong className="text-capitalize">
															{name}
														</strong>
													</div>
												</Popover.Body>
											</Popover>
										) : undefined
									}
								>
									{record.examiner ? (
										<span className="text-capitalize text-limit" role="button">
											{name}
										</span>
									) : (
										<span className="invisible">John Doe</span>
									)}
								</PartogramView.TableData>
							);
						})}
					</tr>
				</PartogramView.Table>
			</PartogramView>
		</>
	);
}
